import {
  Component, OnInit, Inject, OnDestroy
} from '@angular/core';
import {
  Subject, mergeMap, catchError,
  takeUntil,
  Observable,
  take,
  combineLatest,
  ReplaySubject,
  first,
  of,
} from 'rxjs';
import { ActivatedRoute, Params } from '@angular/router';

import { AmplitudeService } from '@sondermind/amplitude';
import {
  buildIntakeEndpoint,
  WizardService,
  FLOWS_INTAKE_SLUGS,
  MAIN_INTAKE_FLOW_SLUG
} from '@sondermind/flows-host';
import { GtmDataLayerService, GtmCustomEvents } from '@sondermind/google-tag-manager';

import {
  FlowsLaunchDarklyService,
  ILaunchDarklyUser,
  LAUNCH_DARKLY_SERVICE,
  UserType,
  LDStringFeatureFlags,
  LDBooleanFeatureFlags
} from '@sondermind/launch-darkly';
import { BrowserNavButtonsService } from '@sondermind/utilities/browser-nav-buttons';
import { FlowInfo } from '@sondermind/utilities/models-flows';
import { UserSystemInfoService } from '@sondermind/utilities/services';

enum QuickIntakeV2FlagValues {
  CONTROL = 'Control',
  ONE_PAGE = 'one-page',
  SOCIAL_SIGN_UP = 'social-signup'
}

enum IntakeViewValues {
  CONTROL = 'control',
  ONE_PAGE = 'one-page',
  SOCIAL_SIGN_UP = 'social-sign-up'
}

@Component({
  selector: 'sonder-flow-boot',
  templateUrl: './app-boot.component.html',
  styleUrls: ['./app-boot.component.scss'],
})
export class AppBootComponent implements OnInit, OnDestroy {
  slug: string = MAIN_INTAKE_FLOW_SLUG;

  // slug that comes from LaunchDarkly
  ldSlugLoaded: boolean = false;
  // needed to compare and prevent non-default flows from being overriden by LaunchDarkly tests
  defaultMatchFlow: string = MAIN_INTAKE_FLOW_SLUG;
  canOverrideBaseFlow: boolean = false;
  private flowsSupportingNavButtons: string[] = [
    MAIN_INTAKE_FLOW_SLUG,
    'matching-flow-skip-first-question',
    'directory-matching-flow'
  ];

  quickIntakeV2FlagValues = QuickIntakeV2FlagValues;
  quickIntakeV2Flag$: Observable<string> = of(QuickIntakeV2FlagValues.CONTROL);

  intakeViewValues = IntakeViewValues;
  intakeView: string = IntakeViewValues.CONTROL;

  shouldUseFlowsIntake: boolean = true;
  private matchFlowLoadedEventSubject: Subject<FlowInfo> = new ReplaySubject<FlowInfo>(1);
  private destroyedSubj: Subject<void> = new Subject<void>();
  destroyed$: Observable<void> = this.destroyedSubj.asObservable();

  constructor(
    public wizardSvc: WizardService,
    private gtmDataLayerService: GtmDataLayerService,
    @Inject(LAUNCH_DARKLY_SERVICE)
    private launchDarklyService: FlowsLaunchDarklyService,
    private userSystemInfoService: UserSystemInfoService,
    private browserNavButtonsService: BrowserNavButtonsService,
    private amplitudeService: AmplitudeService,
    private route: ActivatedRoute,
  ) {
    this.slug = this.defaultMatchFlow;
  }

  ngOnInit(): void {
    this.initializeLaunchDarkly();

    this.quickIntakeV2Flag$ = this.launchDarklyService.checkFlagValue$(
      LDStringFeatureFlags.QUICK_INTAKE_V2, QuickIntakeV2FlagValues.CONTROL);

    combineLatest({
      flowInfo: this.matchFlowLoadedEventSubject,
      quickIntakeV2LdFlag: this.quickIntakeV2Flag$
    }).pipe(
      take(1)
    ).subscribe((results) => {
      if (
        results.flowInfo?.slug === MAIN_INTAKE_FLOW_SLUG &&
        results.quickIntakeV2LdFlag === QuickIntakeV2FlagValues.ONE_PAGE
      ) {
        this.intakeView = IntakeViewValues.ONE_PAGE;
        this.amplitudeService.setupSessionReplay().catch(() => { });
      } else if (
        results.flowInfo?.slug === MAIN_INTAKE_FLOW_SLUG &&
        results.quickIntakeV2LdFlag === QuickIntakeV2FlagValues.SOCIAL_SIGN_UP
      ) {
        this.intakeView = IntakeViewValues.SOCIAL_SIGN_UP;
        this.amplitudeService.setupSessionReplay().catch(() => { });
      } else if (results.quickIntakeV2LdFlag === QuickIntakeV2FlagValues.CONTROL) {
        this.intakeView = IntakeViewValues.CONTROL;
        this.setupMatchFlow();
      }
    });
  }

  ngOnDestroy(): void {
    this.launchDarklyService.closeClient();
    // even-though browserNavButtonsService init() is called conditionally,
    //  it is safe to unconditionally call destroy()
    this.browserNavButtonsService.destroy();
    this.wizardSvc.detachBrowserButtonService();

    this.destroyedSubj.next();
    this.destroyedSubj.complete();
  }

  private getFlowInfoFromQueryParameters(): FlowInfo {
    const urlParams = new URLSearchParams(window.location.search);

    return new FlowInfo(
      urlParams.get('slug'),
      urlParams.get('hash'),
      urlParams.get('strict') === 'yes',
    );
  }

  private setupMatchFlow(): void {
    this.wizardSvc.response$.pipe(
      takeUntil(this.destroyed$)
    ).subscribe(
      (resp) => {
        this.shouldUseFlowsIntake = FLOWS_INTAKE_SLUGS.includes(resp?.slug);
      }
    );

    this.canOverrideBaseFlow = this.slug === this.defaultMatchFlow;

    this.wizardSvc.toggleRunning(true);
    this.initializeMatchFlow();

    if (this.flowsSupportingNavButtons.includes(this.slug)) {
      this.browserNavButtonsService.init();
      this.wizardSvc.attachBrowserButtonService(this.browserNavButtonsService);
    }
  }

  /**
   * Initializing the match flow using either
   * the default slug (supplied via "slug" attribute of "sonder-flow-boot" element)
   * or an override value from LaunchDarkly
   *
   * @private
   * @memberof AppBootComponent
   */
  private initializeMatchFlow(): void {
    this.setMatchFlowDefaultOnDataLayer();
    const matchFlowInitAttempt = this.matchFlowLoadedEventSubject.asObservable().pipe(
      mergeMap((flowInfo) => this.wizardSvc.initialize(
        flowInfo.slug,
        { endpoint_builder: buildIntakeEndpoint },
        flowInfo.hash,
        flowInfo.shouldLoadStrict
      )),
      catchError(() => this.wizardSvc.initialize(this.slug, { endpoint_builder: buildIntakeEndpoint }))
    );

    // set up wizard initialization subscription
    //   so can react and 'start' a flow
    matchFlowInitAttempt.subscribe(() => {
      if (this.wizardSvc.started) {
        this.wizardSvc.start([], true).subscribe();
      }

      // NB: launchDarklyService.initClient() will be called before
      //     reaching this block of code
      const matchFlowInitData = [
        ...this.launchDarklyService.getInitData(),
        { human: 'Matchflow Domain', key: 'domain', value: window.location.hostname },
        { human: 'Document Referrer', key: 'referrer', value: document.referrer },
        { human: 'Path Name', key: 'pathname', value: window.location.pathname },
      ];
      const gaId = this.getGoogleAnalyticsClientId();
      if (gaId) {
        matchFlowInitData.push({ human: 'GA Client ID', key: 'ga_id', value: gaId });
      }

      this.wizardSvc.start(matchFlowInitData, true).subscribe(() => {
        this.wizardSvc.toggleRunning(true);
      });
    });
  }

  private getLaunchDarklyClient(params: Params): ILaunchDarklyUser {
    if (params['anonUserId']) {
      return {
        kind: 'user',
        key: params['anonUserId']
      };
    }

    return {
      anonymous: true,
      custom:
      {
        userType: UserType.CLIENT
      }
    };
  }

  private initializeLaunchDarkly(): void {
    this.route.queryParams.pipe(take(2)).subscribe((params) => {
      this.launchDarklyService.initClient(this.getLaunchDarklyClient(params));
    });

    this.launchDarklyService.clientLoaded.pipe(
      first((loaded) => loaded !== undefined),
      mergeMap(() => this.launchDarklyService.checkFlagEnabled$(LDBooleanFeatureFlags.SKIP_FIRST_MATCHFLOW_QUESTION))
    ).subscribe((shouldSkipFirstPage) => {
      if (shouldSkipFirstPage) {
        this.slug = 'matching-flow-skip-first-question';
      }

      const flowInfo = this.getFlowInfoFromQueryParameters();

      // If there is the slug query string parameter passed in, skip checking LaunchDarkly
      if (!flowInfo.slug &&
        this.canOverrideBaseFlow && this.launchDarklyService.ldSlug?.length > 0 && !this.ldSlugLoaded
      ) {
        this.ldSlugLoaded = true;

        // NOTE: if taking slug from LD, ignore the version hash from URL
        flowInfo.slug = this.launchDarklyService.ldSlug;
        flowInfo.hash = null;
      }

      if (!flowInfo.slug) {
        flowInfo.slug = this.slug;
      }

      // at this point, we should have a slug
      // if don't have version hash, check LaunchDarkly
      if (!flowInfo.hash) {
        const flowVersionFlagName = `flow-version-${flowInfo.slug}`;
        flowInfo.hash = this.launchDarklyService.client?.variation(flowVersionFlagName, '') as string;
      }

      // trigger initialization from slug attribute
      this.matchFlowLoadedEventSubject.next(flowInfo);
    });
  }

  /**
   * Method for setting the match flow default custom event with user system metadata.
   *
   * @private
   * @memberof AppBootComponent
   */
  private setMatchFlowDefaultOnDataLayer(): void {
    const browserInfo = this.userSystemInfoService.userSystemInfo.browser;
    const platformInfo = this.userSystemInfoService.userSystemInfo.platform;
    /* eslint-disable no-extra-boolean-cast */
    const browser = !!browserInfo ? `${browserInfo.name} ${browserInfo.version}` : '';
    const platform = !!platformInfo ? `${platformInfo.type} ${platformInfo.vendor}` : '';
    /* eslint-enable no-extra-boolean-cast */

    this.gtmDataLayerService.dataLayer = {
      event: GtmCustomEvents.MATCH_FLOW_DEFAULT,
      category: GtmCustomEvents.MATCH_FLOW_DEFAULT,
      action: browser,
      label: platform
    };
  }

  private getGoogleAnalyticsClientId(): string {
    // Regex to get the _ga cookie value from the document.cookie string
    let match = /(?:^|;)\s*_ga=([^;]*)/.exec(document.cookie);
    const raw = match ? decodeURIComponent(match[1]) : null;
    if (raw) {
      match = /(\d+\.\d+)$/.exec(raw);
    }
    return match ? match[1] : null;
  }
}
